import { useState, useEffect } from 'react';
import { api } from '../services/api';

interface BracketTeam {
  id: string;
  name: string;
  shortName: string;
  seed?: number;
  region?: string;
}

interface BracketMatchup {
  id: string;
  round: string;
  team1?: BracketTeam;
  team2?: BracketTeam;
  winner?: { id: string; name: string };
  homeScore?: number;
  awayScore?: number;
  isFinal: boolean;
  scheduledAt: string;
}

interface TournamentBracketViewProps {
  poolId: string;
  compact?: boolean;
}

const ROUND_ORDER = [
  'Round of 64',
  'Round of 32',
  'Sweet 16',
  'Elite 8',
  'Final Four',
  'Championship',
];

export function TournamentBracketView({ poolId, compact = false }: TournamentBracketViewProps) {
  const [matchups, setMatchups] = useState<Record<string, BracketMatchup[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadMatchups();
  }, [poolId]);

  const loadMatchups = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getBracketMatchupsPublic(poolId);
      setMatchups(data || {});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bracket');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-gray-600 text-sm">Loading bracket...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
        {error}
      </div>
    );
  }

  const hasMatchups = Object.keys(matchups).length > 0;
  const roundsWithMatchups = ROUND_ORDER.filter(r => matchups[r]?.length > 0);

  if (!hasMatchups) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
        <p className="text-amber-800 font-semibold">Tournament bracket coming soon</p>
        <p className="text-amber-700 text-sm mt-1">
          Check back after Selection Sunday when the field is announced
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <span>🏀</span>
          Tournament Bracket
        </h2>
        <p className="text-blue-100 text-sm mt-1">
          {roundsWithMatchups.length} round{roundsWithMatchups.length !== 1 ? 's' : ''} in progress
        </p>
      </div>

      {/* Bracket Content */}
      <div className={`p-6 space-y-8 ${compact ? 'space-y-6' : ''}`}>
        {roundsWithMatchups.map(round => (
          <div key={round}>
            {/* Round Title */}
            <div className="mb-4 pb-3 border-b-2 border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">{round}</h3>
              <p className="text-xs text-gray-500 mt-1">
                {matchups[round].length} matchup{matchups[round].length !== 1 ? 's' : ''}
              </p>
            </div>

            {/* Matchups Grid */}
            <div className={`grid gap-4 ${
              compact ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
            }`}>
              {matchups[round].map((matchup) => (
                <div
                  key={matchup.id}
                  className="border border-gray-300 rounded-lg overflow-hidden hover:border-gray-400 transition-colors"
                >
                  {/* Matchup Header */}
                  {matchup.scheduledAt && (
                    <div className="bg-gray-50 px-4 py-2 border-b border-gray-300">
                      <p className="text-xs text-gray-600">
                        {new Date(matchup.scheduledAt).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  )}

                  {/* Teams */}
                  <div className={`divide-y divide-gray-300 ${matchup.scheduledAt ? '' : 'pt-2'}`}>
                    {/* Team 1 */}
                    <PublicTeamSlot
                      team={matchup.team1}
                      score={matchup.homeScore}
                      isWinner={matchup.winner?.id === matchup.team1?.id}
                      isFinal={matchup.isFinal}
                      compact={compact}
                    />

                    {/* Team 2 */}
                    <PublicTeamSlot
                      team={matchup.team2}
                      score={matchup.awayScore}
                      isWinner={matchup.winner?.id === matchup.team2?.id}
                      isFinal={matchup.isFinal}
                      compact={compact}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="bg-gray-50 border-t border-gray-200 px-6 py-3">
        <p className="text-xs text-gray-600">
          Updates automatically. Last updated{' '}
          <span className="font-medium">
            {new Date().toLocaleTimeString(undefined, {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </p>
      </div>
    </div>
  );
}

interface PublicTeamSlotProps {
  team?: BracketTeam;
  score?: number;
  isWinner: boolean;
  isFinal: boolean;
  compact?: boolean;
}

function PublicTeamSlot({
  team,
  score,
  isWinner,
  isFinal,
}: PublicTeamSlotProps) {
  const isTBD = !team || team.name.includes('TBD');

  return (
    <div
      className={`
        flex items-center justify-between gap-2 px-4 py-3 transition-colors
        ${isWinner ? 'bg-green-50' : isTBD ? 'bg-gray-50' : 'bg-white'}
        ${isWinner && isFinal ? 'border-l-4 border-green-500' : ''}
      `}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {team ? (
          <>
            <span
              className={`
                w-6 h-6 flex items-center justify-center rounded text-xs font-bold flex-shrink-0
                ${isTBD ? 'bg-gray-300 text-gray-500' : 'bg-blue-600 text-white'}
              `}
            >
              {team.seed || '?'}
            </span>
            <span className={`text-sm truncate ${
              isWinner && isFinal ? 'font-semibold text-green-700' : 'text-gray-900'
            } ${isTBD ? 'text-gray-400 italic' : ''}`}>
              {isTBD ? 'To Be Determined' : team.name}
            </span>
          </>
        ) : (
          <span className="text-gray-400 italic text-sm">TBD</span>
        )}
      </div>
      {isFinal && score !== undefined && (
        <span className={`text-lg font-bold flex-shrink-0 w-8 text-right ${
          isWinner ? 'text-green-700' : 'text-gray-400'
        }`}>
          {score}
        </span>
      )}
    </div>
  );
}

export default TournamentBracketView;
